import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, Smartphone, Heart, MessageCircle, BarChart2, Zap, UserPlus, Radio, Globe, Ghost, MessageSquare } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';

const DEFAULT_IN_APP_PREFERENCES = {
    inAppNotifications: true,
  
    // Shadows
    inAppShadowLikes: true,
    inAppShadowComments: true,
    inAppShadowPolls: true,
    inAppShadowMentions: true,
    inAppShadowCommentMentions: true,
    
    // Posts
    inAppPostLikes: true,
    inAppPostComments: true,
    inAppPostPolls: true,
    inAppPostMentions: true,
    inAppCommentMentions: true,
    
    // Network
    inAppNewFollowers: true,
    
    // Anon
    inAppCampusAlerts: true,
    inAppGlobalAlerts: true,
    inAppMessages: true,
  
    // Activity
    inAppBroadcastFollowerPosts: true,
    inAppBroadcastCampusPosts: true,
    inAppBroadcastCampusShadows: true,
};

export const InAppNotificationsSettingsPage = () => {
    const navigate = useNavigate();
    const currentUser = auth.currentUser;

    const [loading, setLoading] = useState(true);
    const [preferences, setPreferences] = useState(DEFAULT_IN_APP_PREFERENCES);

    useEffect(() => {
        const fetchPreferences = async () => {
            if (!currentUser) return;
            try {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                if (userDoc.exists() && userDoc.data().inAppPreferences) {
                    setPreferences({
                        ...DEFAULT_IN_APP_PREFERENCES,
                        ...userDoc.data().inAppPreferences
                    });
                }
            } catch (err) {
                console.error("Failed to load in-app preferences:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchPreferences();
    }, [currentUser]);

    const handleToggle = async (key, value) => {
        const newPrefs = { ...preferences, [key]: value };
        setPreferences(newPrefs);
        
        if (!currentUser) return;
        
        try {
            await setDoc(doc(db, 'users', currentUser.uid), {
                inAppPreferences: newPrefs
            }, { merge: true });
        } catch (err) {
            console.error("Failed to save in-app prefs:", err);
            setPreferences(preferences);
        }
    };

    const sections = [
        /* Post Activity — Disabled until Reality mode is live
        {
            title: 'Post Activity',
            data: [
                { key: 'inAppPostLikes', label: 'Likes', icon: Heart },
                { key: 'inAppPostComments', label: 'Comments', icon: MessageCircle },
                { key: 'inAppPostMentions', label: 'Mentions in Posts', icon: Zap },
                { key: 'inAppCommentMentions', label: 'Mentions in Comments', icon: Zap },
                { key: 'inAppPostPolls', label: 'Poll Votes', icon: BarChart2 },
            ]
        },
        */
        {
            title: 'Shadow Activity',
            data: [
                { key: 'inAppShadowLikes', label: 'Likes', icon: Heart },
                { key: 'inAppShadowComments', label: 'Comments', icon: MessageCircle },
                { key: 'inAppShadowPolls', label: 'Poll Votes', icon: BarChart2 },
                { key: 'inAppShadowMentions', label: 'Mentions in Shadows', icon: Zap },
                { key: 'inAppShadowCommentMentions', label: 'Mentions in Comments', icon: Zap },
            ]
        },
        /* Network — Disabled until Reality mode is live
        {
            title: 'Network',
            data: [
                { key: 'inAppNewFollowers', label: 'New Followers & Requests', icon: UserPlus },
            ]
        },
        */
        {
            title: 'New Content Alerts',
            data: [
                // { key: 'inAppBroadcastCampusPosts', label: 'Campus Posts', icon: Radio },
                // { key: 'inAppBroadcastFollowerPosts', label: 'Follower Posts', icon: UserPlus },
                { key: 'inAppBroadcastCampusShadows', label: 'Campus Shadows', icon: Ghost },
            ]
        },
        {
            title: 'Anonymous Chat',
            data: [
                { key: 'inAppCampusAlerts', label: 'Campus Alerts', icon: Zap },
                { key: 'inAppGlobalAlerts', label: 'Global Alerts', icon: Globe },
                { key: 'inAppMessages', label: 'Chat Messages', icon: MessageSquare },
            ]
        }
    ];

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center min-h-screen bg-[#0a0a0a]">
                <div className="w-8 h-8 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
            </div>
        );
    }

    const isDisabled = !preferences.inAppNotifications;

    return (
        <main className="flex-1 w-full flex flex-col bg-transparent">
            {/* Header */}
            <div className="sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-md z-40 border-b border-[#1a1a1a] flex items-center h-[60px] px-4">
                <button 
                    onClick={() => navigate('/settings/notifications')}
                    className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2"
                >
                    <ChevronLeft size={24} className="text-white" />
                </button>
                <h1 className="text-xl font-bold text-white">In-App Notifications</h1>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-20 sm:pb-0">
                
                {/* Master Switch */}
                <div className="bg-[#111] border border-[#222] rounded-2xl overflow-hidden mb-6">
                    <div className="p-4 sm:p-5 flex items-center justify-between">
                        <div className="flex items-center flex-1 mr-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${preferences.inAppNotifications ? 'bg-[#0BD9E5]/20 text-[#0BD9E5]' : 'bg-gray-800 text-gray-400'}`}>
                                <Smartphone size={20} />
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-[16px]">Allow Banners</h3>
                                <p className="text-gray-400 text-[13px] mt-0.5 leading-tight">
                                    Show notifications while in app
                                </p>
                            </div>
                        </div>
                        
                        <button 
                            onClick={() => handleToggle('inAppNotifications', !preferences.inAppNotifications)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${preferences.inAppNotifications ? 'bg-[#0BD9E5]' : 'bg-gray-700'}`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences.inAppNotifications ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </div>

                <div className={`space-y-6 ${isDisabled ? 'opacity-50 pointer-events-none grayscale' : 'transition-opacity'}`}>
                    {sections.map((section) => (
                        <div key={section.title}>
                            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">{section.title}</h2>
                            <div className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
                                {section.data.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <div key={item.key} className={`p-4 flex items-center justify-between ${index !== section.data.length - 1 ? 'border-b border-[#222]' : ''}`}>
                                            <div className="flex items-center">
                                                <Icon size={20} className="text-[#0BD9E5] mr-3" />
                                                <span className="text-white font-medium">{item.label}</span>
                                            </div>
                                            <button 
                                                onClick={() => handleToggle(item.key, !preferences[item.key])}
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${preferences[item.key] ? 'bg-[#0BD9E5]' : 'bg-gray-700'}`}
                                            >
                                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${preferences[item.key] ? 'translate-x-5' : 'translate-x-1'}`} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </main>
    );
};

export default InAppNotificationsSettingsPage;
